import {
    CreateGHGEntryModal,
    GHGResultBySourceGraph,
    GHGResultByYearGraph,
    GHGScopeDisplay,
    type GHGScopeData,
} from "@dmca/frontend/widgets/ghg";
import { PageHeader } from "@dmca/frontend/components/nav";
import { checkPermissionsInLoader } from "@dmca/frontend/hooks";
import { useTRPC } from "@dmca/frontend/trpc";
import { Box, Fab, Link, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { DateTime } from "@dmca/utils/luxon";

export const Route = createFileRoute("/_authed/ghg")({
    component: RouteComponent,
    loader: async ({ context }) => {
        await checkPermissionsInLoader("GHG::ViewGHG", context);
        const { trpcQcProxy: trpc, qc } = context;
        qc.prefetchQuery(trpc.ghg.getResources.queryOptions());
        qc.prefetchQuery(trpc.ghg.findLastReport.queryOptions());
    },
    head: () => ({
        meta: [
            {
                title: "Greenhouse Gas Emissions | DNA Dashboard",
            },
        ],
    }),
});

// Defined outside component to prevent infinite rerendering
const now = DateTime.now() as DateTime<true>;
const yearStart = DateTime.fromObject({ year: 2019 }) as DateTime<true>;
const yearEnd = DateTime.fromObject({ year: 2030 }) as DateTime<true>;

function RouteComponent() {
    const [showAddModal, setShowAddModal] = useState(false);

    const trpc = useTRPC();

    const { data: emissions } = useQuery(
        trpc.ghg.findCO2ePartitionByTime.queryOptions({
            start: yearStart as DateTime<true>,
            end: yearEnd as DateTime<true>,
            timePartition: "year",
        })
    );

    const { data: emissionsByMonth } = useQuery(
        trpc.ghg.findCO2ePartitionByTime.queryOptions({
            start: now.minus({ months: 12 }).startOf("month") as DateTime<true>,
            end: now.minus({ months: 1 }).startOf("month") as DateTime<true>,
            timePartition: "month",
        })
    );

    const yearEmissions = Object.entries(emissions ?? {}).map(([time, data]) => ({
        year: DateTime.fromMillis(Number(time) * 1000, { zone: "utc" }).year,
        data,
    }));

    const baseline = yearEmissions[0]?.data;
    const currentYearIndex = Math.max(0, yearEmissions.findIndex((e) => e.year === now.year));
    const currentData = yearEmissions[currentYearIndex]?.data;

    const reductionFactor = (index: number) => 1 - 0.025 * index;

    const yearData = yearEmissions.map(({ year, data }, index) => ({
        year,
        value: Math.round(data.total / 1000),
        target: Math.round(((baseline?.total ?? 0) / 1000) * reductionFactor(index)),
    }));

    const ghgScopeData: GHGScopeData = baseline
        ? {
              scope1: {
                  actual: currentData
                      ? Math.round(((currentData.gas ?? 0) + (currentData.lpg ?? 0)) / 1000)
                      : 0,
                  target: Math.round(
                      (((baseline.gas ?? 0) + (baseline.lpg ?? 0)) / 1000) *
                          reductionFactor(currentYearIndex)
                  ),
              },
              scope2: {
                  actual: currentData
                      ? Math.round((currentData.electrical ?? 0) / 1000)
                      : 0,
                  target: Math.round(
                      ((baseline.electrical ?? 0) / 1000) * reductionFactor(currentYearIndex)
                  ),
              },
              scope3: {
                  suppliers: {
                      actual: currentData
                          ? Math.round((currentData.water ?? 0) / 1000)
                          : 0,
                      target: Math.round(
                          ((baseline.water ?? 0) / 1000) * reductionFactor(currentYearIndex)
                      ),
                  },
              },
          }
        : {};

    const emissionSourceData = Object.entries(emissionsByMonth ?? {}).map(
        ([time, data]) => {
            const { total: _total, ...rest } = data;
            return {
                time: Number(time),
                ...Object.fromEntries(
                    Object.entries(rest).map(([key, value]) => [
                        key === "lpg" ? "LPG" : key.charAt(0).toUpperCase() + key.slice(1),
                        Math.round(value / 1000),
                    ])
                ),
            };
        }
    );

    return (
        <Box sx={{ position: "relative", minHeight: "calc(100vh - 100px)" }}>
            <PageHeader>Greenhouse Gas Emissions</PageHeader>
            <Stack direction="column" gap={1}>
                <GHGScopeDisplay data={ghgScopeData} />
                {emissions && (
                    <Box sx={{ height: 400 }}>
                        <GHGResultByYearGraph data={yearData} />
                    </Box>
                )}
                {emissionSourceData.length > 0 && (
                    <Box sx={{ height: 400, mb: 2 }}>
                        <GHGResultBySourceGraph data={emissionSourceData} />
                    </Box>
                )}
                <Link
                    variant="subtitle2"
                    href="https://www.canada.ca/en/environment-climate-change/services/climate-change/pricing-pollution-how-it-will-work/output-based-pricing-system/federal-greenhouse-gas-offset-system/emission-factors-reference-values.html"
                    textAlign="center"
                    target="_blank"
                    color="text.disabled"
                    rel="noreferrer"
                >
                    GHG Sources
                </Link>
            </Stack>
            <Fab
                color="primary"
                data-testid="ghg-add-entry-fab"
                sx={{ position: "fixed", right: 16, bottom: 16 }}
                onClick={() => setShowAddModal(true)}
            >
                <AddIcon />
            </Fab>
            <CreateGHGEntryModal
                open={showAddModal}
                onClose={() => setShowAddModal(false)}
            />
        </Box>
    );
}
